import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ClipboardList, Eye, Globe2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { SectionHeading } from "../components/Motifs";
import { useLang } from "../lib/i18n";

const STEPS = [
  { icon: ClipboardList, tKey: "sub.step1.t", xKey: "sub.step1.x" },
  { icon: Eye, tKey: "sub.step2.t", xKey: "sub.step2.x" },
  { icon: CheckCircle2, tKey: "sub.step3.t", xKey: "sub.step3.x" },
];

const ACTION_FIELDS = ["order_online", "menu", "book", "reservation", "quote", "shop"];

const EMPTY = {
  name: "", category: "", description: "", phone: "", email: "", website: "", price: "", event_date: "",
  address: { line1: "", city: "", state: "", postal: "", country: "US" },
  images: "", languages: "", hours: "", submitted_by: { name: "", email: "" },
  actions: {},
};

export default function SubmitListing() {
  const { t, tOr } = useLang();
  const [categories, setCategories] = useState([]);
  const [countries, setCountries] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data || [])).catch(() => {});
    api.get("/countries").then((r) => setCountries((r.data || []).filter((c) => c.status === "active"))).catch(() => {});
  }, []);

  const usStates = countries.find((c) => c.code === form.address.country)?.states || [];
  const cities = usStates.find((s) => s.name === form.address.state)?.cities || [];
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setAddr = (k, v) => setForm((f) => ({ ...f, address: { ...f.address, [k]: v } }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const hours = {};
      form.hours.split("\n").forEach((line) => {
        const [d, ...rest] = line.split(":");
        if (d && rest.length) hours[d.trim()] = rest.join(":").trim();
      });
      await api.post("/listings", {
        ...form,
        hours,
        images: form.images.split("\n").map((s) => s.trim()).filter(Boolean),
        languages: form.languages.split(",").map((s) => s.trim()).filter(Boolean),
        actions: Object.fromEntries(Object.entries(form.actions).filter(([, v]) => v)),
      });
      setSent(true);
      toast.success(t("toast.sub.ok"));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full px-4 py-3 rounded-xl bg-sand/60 text-sm outline-none border border-transparent focus:border-gold";

  if (sent) {
    return (
      <div className="py-24 lg:py-32 bg-cream acheik-bg" data-testid="submit-success">
        <div className="max-w-xl mx-auto px-4 text-center">
          <CheckCircle2 className="w-14 h-14 text-jade mx-auto mb-6" />
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-ink mb-4">{t("sub.success.t")}</h1>
          <p className="text-clay leading-relaxed mb-8">{t("sub.success.x")}</p>
          <div className="flex justify-center gap-4">
            <Link to="/directory" className="bg-ruby text-cream text-sm font-semibold px-6 py-3 rounded-full" data-testid="success-browse-btn">{t("sub.browse")}</Link>
            <button onClick={() => { setForm(EMPTY); setSent(false); }} className="border border-[#E6DED3] text-ink text-sm font-semibold px-6 py-3 rounded-full" data-testid="success-submit-another-btn">
              {t("sub.another")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="submit-listing-page">
      <section className="bg-teak acheik-bg-dark text-cream py-14 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-gold mb-4">{t("sub.free")}</p>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight" data-testid="submit-title">
            {t("sub.title.a")}<span className="font-serif italic text-gold">{t("sub.title.b")}</span>
          </h1>
          <p className="mt-4 text-cream/60 max-w-xl">{t("sub.intro")}</p>
        </div>
      </section>

      <section className="py-14 lg:py-20 bg-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4 space-y-6">
            <SectionHeading overline={t("sub.how")} title={t("sub.steps")} />
            {STEPS.map((s, i) => (
              <div key={s.tKey} className="flex gap-4 bg-white border border-[#E6DED3] rounded-2xl p-5" data-testid={`step-${i}`}>
                <span className="w-10 h-10 rounded-full bg-gold-light border border-gold/40 flex items-center justify-center text-gold-dark shrink-0">
                  <s.icon className="w-5 h-5" />
                </span>
                <div>
                  <p className="font-display font-bold text-ink text-sm">{i + 1}. {t(s.tKey)}</p>
                  <p className="text-xs text-clay mt-1 leading-relaxed">{t(s.xKey)}</p>
                </div>
              </div>
            ))}
            <p className="flex items-start gap-2 text-xs text-stone2 leading-relaxed">
              <Globe2 className="w-4 h-4 shrink-0 mt-0.5 text-gold-dark" />
              {t("sub.open")}
            </p>
          </div>

          <form onSubmit={submit} className="lg:col-span-8 bg-white border border-[#E6DED3] rounded-2xl p-6 sm:p-10 space-y-8" data-testid="submit-listing-form">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ruby mb-4">{t("sub.basics")}</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <select required value={form.category} onChange={(e) => set("category", e.target.value)} className={inputCls} data-testid="submit-category-select">
                  <option value="">{t("sub.what")}</option>
                  {categories.map((c) => <option key={c.slug} value={c.slug}>{tOr(`cat.${c.slug}`, c.label)}</option>)}
                </select>
                <input required value={form.name} onChange={(e) => set("name", e.target.value)} placeholder={t("sub.name")} className={inputCls} data-testid="submit-name-input" />
              </div>
              <textarea required value={form.description} onChange={(e) => set("description", e.target.value)} placeholder={t("sub.desc")} rows={4} className={`${inputCls} mt-4`} data-testid="submit-description-input" />
              <div className="grid sm:grid-cols-2 gap-4 mt-4">
                <input value={form.price} onChange={(e) => set("price", e.target.value)} placeholder={t("sub.price")} className={inputCls} data-testid="submit-price-input" />
                {form.category === "events" && (
                  <input type="date" value={form.event_date} onChange={(e) => set("event_date", e.target.value)} className={inputCls} data-testid="submit-event-date-input" />
                )}
              </div>
            </div>

            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ruby mb-4">{t("sub.location")}</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <select value={form.address.country} onChange={(e) => setAddr("country", e.target.value)} className={inputCls} data-testid="submit-country-select">
                  {countries.map((c) => <option key={c.code} value={c.code}>{tOr(`country.${c.code}`, c.name)}</option>)}
                </select>
                <select value={form.address.state} onChange={(e) => setAddr("state", e.target.value)} className={inputCls} data-testid="submit-state-select">
                  <option value="">{t("sub.state")}</option>
                  {usStates.map((s) => <option key={s.name} value={s.name}>{s.name}</option>)}
                </select>
                <select value={form.address.city} onChange={(e) => setAddr("city", e.target.value)} className={inputCls} data-testid="submit-city-select">
                  <option value="">{t("sub.city")}</option>
                  {cities.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <input value={form.address.postal} onChange={(e) => setAddr("postal", e.target.value)} placeholder={t("sub.postal")} className={inputCls} data-testid="submit-postal-input" />
              </div>
              <input value={form.address.line1} onChange={(e) => setAddr("line1", e.target.value)} placeholder={t("sub.street")} className={`${inputCls} mt-4`} data-testid="submit-address-input" />
            </div>

            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ruby mb-4">{t("sub.contact")}</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder={t("sub.phone")} className={inputCls} data-testid="submit-phone-input" />
                <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder={t("sub.email")} className={inputCls} data-testid="submit-email-input" />
                <input value={form.website} onChange={(e) => set("website", e.target.value)} placeholder={t("sub.website")} className={inputCls} data-testid="submit-website-input" />
                <input value={form.languages} onChange={(e) => set("languages", e.target.value)} placeholder={t("sub.langs")} className={inputCls} data-testid="submit-languages-input" />
              </div>
              <textarea value={form.hours} onChange={(e) => set("hours", e.target.value)} placeholder={t("sub.hours.ph")} rows={3} className={`${inputCls} mt-4`} data-testid="submit-hours-input" />
              <textarea value={form.images} onChange={(e) => set("images", e.target.value)} placeholder={t("sub.photos.ph")} rows={2} className={`${inputCls} mt-4`} data-testid="submit-images-input" />
            </div>

            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ruby mb-4">{t("sub.actions")}</p>
              <div className="grid sm:grid-cols-2 gap-4">
                {ACTION_FIELDS.map((key) => (
                  <input
                    key={key}
                    value={form.actions[key] || ""}
                    onChange={(e) => setForm((f) => ({ ...f, actions: { ...f.actions, [key]: e.target.value } }))}
                    placeholder={t(`actf.${key}`)}
                    className={inputCls}
                    data-testid={`submit-action-${key}`}
                  />
                ))}
              </div>
            </div>

            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ruby mb-4">{t("sub.yourcontact")}</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <input required value={form.submitted_by.name} onChange={(e) => setForm((f) => ({ ...f, submitted_by: { ...f.submitted_by, name: e.target.value } }))} placeholder={t("sub.yourname")} className={inputCls} data-testid="submit-contact-name-input" />
                <input required type="email" value={form.submitted_by.email} onChange={(e) => setForm((f) => ({ ...f, submitted_by: { ...f.submitted_by, email: e.target.value } }))} placeholder={t("sub.youremail")} className={inputCls} data-testid="submit-contact-email-input" />
              </div>
            </div>

            <button
              type="submit"
              disabled={busy}
              className="w-full bg-ruby hover:bg-ruby-dark disabled:opacity-60 text-cream font-bold py-4 rounded-full transition-colors shadow-[0_10px_30px_rgba(139,0,24,0.3)]"
              data-testid="submit-listing-btn"
            >
              {busy ? t("sub.submitting") : t("sub.submit")}
            </button>
            <p className="text-center text-xs text-stone2">
              {t("form.agree")} <Link to="/privacy-policy" className="text-ruby font-semibold hover:text-gold-dark" data-testid="submit-privacy-link">{t("footer.privacy")}</Link>{" & "}<Link to="/terms-of-service" className="text-ruby font-semibold hover:text-gold-dark" data-testid="submit-terms-link">{t("footer.terms")}</Link>{t("form.agree.end")}
            </p>
          </form>
        </div>
      </section>
    </div>
  );
}
